/**
 * URL hygiene for analytics. GA4 attaches the browser's current URL to EVERY event it sends, not
 * just page views, so keeping secrets out of analytics means keeping the container away from URLs
 * that carry them; trimming the page view alone is not enough. Mirrors src/analytics/location.ts
 * in lpa.ui, so both apps report the same shape into the shared container.
 *
 *  - /confirmPassword and /setPassword carry the customer's email address in the query string
 *    (the set-password page needs it on reload), and /recording-token/<token> carries a secret in
 *    the path. The container is NOT LOADED while the address bar shows one of these, and no page
 *    view is reported for them. It loads on the first safe route instead (analyticsService.ts).
 *  - Marketing parameters (utm_*, click ids) are what GA4 attributes campaigns from, so page views
 *    keep exactly those and drop everything else.
 *  - Ad click ids (gclid and friends) are what Google Ads attributes a purchase to, and its
 *    Conversion Linker reads them from the address bar when the container loads, which, under
 *    strict consent, can be pages after the landing, when the router has long dropped them. So
 *    the ones the visit landed with are kept (in memory, analyticsService.ts) and put back just
 *    before the load.
 */

// Compared case-insensitively: the router matches routes that way too.
const SENSITIVE_PATH_PREFIXES = ['/confirmpassword', '/setpassword', '/recording-token'];
const SENSITIVE_PARAMS = new Set(['token', 'userid', 'email', 'username', 'code', 'password']);
const MARKETING_PARAM = /^(utm_[a-z0-9_]+|gclid|gbraid|wbraid|dclid|fbclid|msclkid|ttclid|li_fat_id)$/i;
// What Google's Conversion Linker reads from the address bar: the Google Ads / Campaign Manager
// click ids, and gclsrc, which tells it where a gclid came from.
const AD_CLICK_PARAMS = new Set(['gclid', 'gclsrc', 'dclid', 'gbraid', 'wbraid']);

// True while the address bar holds something analytics must never see.
export const isSensitiveLocation = (pathname: string, search: string): boolean => {
  const path = pathname.toLowerCase();
  if (SENSITIVE_PATH_PREFIXES.some((prefix) => path.startsWith(prefix))) {
    return true;
  }

  let sensitive = false;
  new URLSearchParams(search).forEach((_value, key) => {
    if (SENSITIVE_PARAMS.has(key.toLowerCase())) {
      sensitive = true;
    }
  });
  return sensitive;
};

// The marketing parameters of `search`, as a query string without the leading "?".
export const marketingQuery = (search: string): string => {
  const kept = new URLSearchParams();
  new URLSearchParams(search).forEach((value, key) => {
    if (MARKETING_PARAM.test(key)) {
      kept.append(key, value);
    }
  });
  return kept.toString();
};

// The ad click ids of `search` (exact names, as the Conversion Linker reads them), without "?".
export const adClickQuery = (search: string): string => {
  const kept = new URLSearchParams();
  new URLSearchParams(search).forEach((value, key) => {
    if (AD_CLICK_PARAMS.has(key)) {
      kept.append(key, value);
    }
  });
  return kept.toString();
};

// origin + pathname + the marketing parameters only: what a page view reports as its URL.
// `landingQuery` adds the campaign the visit ARRIVED with. The affiliate landing and recording-token
// pages navigate on as soon as they mount, which drops the query string before the first page view
// is reported, and GA4 attributes the whole session from that first page view.
export const safePageLocation = (origin: string, pathname: string, search: string, landingQuery = ''): string => {
  const merged = new URLSearchParams(landingQuery);
  new URLSearchParams(marketingQuery(search)).forEach((value, key) => {
    merged.set(key, value);
  });
  const query = merged.toString();
  return `${origin}${pathname}${query ? `?${query}` : ''}`;
};

// Puts ad click ids (adClickQuery) back into the visible URL, where the container's Conversion
// Linker reads them as it loads. No navigation: the history state is preserved as-is, so the router
// (which keeps its keys there) is unaffected, and it is not told about the change either. An id the
// address bar already carries is left as it is.
export const restoreAdClickIds = (query: string): void => {
  if (!query) {
    return;
  }

  const url = new URL(window.location.href);
  let changed = false;
  new URLSearchParams(query).forEach((value, key) => {
    if (!url.searchParams.has(key)) {
      url.searchParams.append(key, value);
      changed = true;
    }
  });
  if (changed) {
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  }
};
